/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import './RsaAuthenticatorInfo.scss';
import {ExdsGrid, ExdsText} from "@exds/core";
import {TuxAccordion, TuxAccordionPanel, TuxLoadingIndicator} from "@tuxedo/react-component-library";
import React, {useContext, useEffect} from 'react';
import ButtonGroup from "../../../ButtonGroup/ButtonGroup";
import RsaHeader from '../RsaHeader/RsaHeader';
import {useNavigate} from 'react-router-dom';
import AppContext from "../../../../AppContext";
import {STEP_SKIPPED} from "../../../../constants/constants";
import Alert from "../../../Alert/Alert";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function RsaAuthenticatorInfo(props) {
  const { setActiveMobileAppSetupPage, setStepStatus, handleModalAction, skipError, setSkipError, setPageName } = props;
  const {hashStepsStatusContext, isSmartLogonEnabledContext, sidContext, loadAA} = useContext(AppContext);
  const sid = sidContext.at(0);
  const hashStepsStatus = hashStepsStatusContext.at(0);
  const isSmartLogonEnabledBool = isSmartLogonEnabledContext.at(0) === "true";
  const navigate = useNavigate();
  const pageName = 'Mobile App Setup | RSA Authenticator Info';

  const modalDetails = {
    title: 'Are you sure you want to skip RSA Setup?',
    modalActionButton: 'Yes, I want to skip',
    continueButton: 'No, lets continue',
    body: 'Skipping RSA Setup will prevent you from accessing certain features.'
  };

  useEffect(() => {
    if(loadAA) {
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSuccessCall = () => {
    setStepStatus('rsaInfo', 'skipped');
    if (isSmartLogonEnabledBool) {
      setActiveMobileAppSetupPage('smartLogOn');
    } else {
      navigate('/finish');
    }
  };

  const handleAdvanceButtonAction = () => {
    setSkipError({ showError: false, correlationId: '', isLoading: false });
    setActiveMobileAppSetupPage('downloadRsa');
  };

  return (
    <>
      {skipError?.isLoading && <div className="loading-spinner"><TuxLoadingIndicator/></div>}
      {!skipError?.isLoading &&
        <ExdsGrid columnSpan={8} className='step-details'>
          {
            (skipError.showError && <Alert correlationId={skipError.correlationId} loadAA={loadAA} pageName={pageName} sid={sid}/>)
          }
          <RsaHeader />
          <ExdsText primitive="div">
            The RSA Authenticator app is used to enhance JPMC security by providing two-factor authentication (2FA). It
            generates a one-time passcode that users must enter along with their regular password to access JPMC secure
            systems or applications.
          </ExdsText>
          <ExdsText primitive="h3" className='faq-header'>FAQ's</ExdsText>
          <TuxAccordion className='rsa-accordion-body'>
            <TuxAccordionPanel title={<ExdsText primitive="h3" className="accordion-title">What is two-factor authentication
              (2FA)?</ExdsText>}>
              <p>Two-factor authentication (2FA) is a security process that requires users to provide two different forms of
                identification to verify their identity, enhancing the security of online accounts.</p>
            </TuxAccordionPanel>
            <TuxAccordionPanel
              title={<ExdsText primitive="h3" className="accordion-title">Do I need RSA Authenticator?</ExdsText>}>
              <p>Most employees require RSA Authenticator in order to access virtual desktops and certain applications.
                However, certain roles, like CCB Branch employees, may not be eligible to request a soft token due to
                compliance reasons. Please contact your manager to verify your eligibility or need for RSA Authenticator.</p>
            </TuxAccordionPanel>
            <TuxAccordionPanel
              title={<ExdsText primitive="h3" className="accordion-title">Can I install RSA Authenticator on more than 1
                device?</ExdsText>}>
              <p>RSA Authenticator uses unique tokens to generate access codes. The number of tokens an employee can hold is
                restricted. It is recommended you install RSA Authenticator on your primary personal device.</p>
            </TuxAccordionPanel>
          </TuxAccordion>
          <ExdsText primitive="div" className='device-requirements'>
            <p className='section-header'>Device requirements:</p>
            <ul>
              <li>Minimum iOS 16 or Android 9</li>
            </ul>
          </ExdsText>
          <ButtonGroup
            modalButtonContent='Skip RSA Setup'
            advanceButtonContent='Continue'
            advanceButtonAction={handleAdvanceButtonAction}
            modalButtonType='link'
            modalDetails={modalDetails}
            modalAction={() => handleModalAction('SKIP_RSA', STEP_SKIPPED, hashStepsStatus.sloSetup, hashStepsStatus.sloTest, handleSuccessCall)}
            backButtonContent='Back'
            backButtonAction={() => setActiveMobileAppSetupPage('deviceSelection')}
          />
        </ExdsGrid>
      }
    </>
  );
}
